import React from 'react';
import "./checkbox.style.css";

interface ICheckboxProps {
    checked?: boolean;
    label?: string;
    onChange?: (checked: boolean) => void;
    className?: string;
    disabled?: boolean;
    size?: 'small' | 'medium' | 'large';
    variant?: 'default' | 'rounded' | 'switch';
    color?: 'blue' | 'emerald' | 'red' | 'amber' | 'purple';
    description?: string;
    required?: boolean;
    error?: string;
    id?: string;
    name?: string;
}

const Checkbox: React.FC<ICheckboxProps> = ({
    checked = false,
    onChange,
    label,
    className = '',
    disabled = false,
    size = 'medium',
    variant = 'default',
    color = 'blue',
    description,
    required = false,
    error,
    id,
    name
}) => {
    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (onChange && !disabled) {
            onChange(event.target.checked);
        }
    };

    const checkboxId = id || name || `checkbox-${Math.random().toString(36).substr(2, 9)}`;

    const sizes = {
        small: {
            checkbox: 'w-4 h-4',
            text: 'text-sm',
            gap: 'gap-2'
        },
        medium: {
            checkbox: 'w-5 h-5',
            text: 'text-base',
            gap: 'gap-3'
        },
        large: {
            checkbox: 'w-6 h-6',
            text: 'text-lg',
            gap: 'gap-4'
        }
    };

    const colors = {
        blue: 'text-[var(--primary-600)] focus:ring-[var(--primary-500)]/20 border-[var(--border-glass-strong)]',
        emerald: 'text-emerald-500 focus:ring-emerald-500/20 border-[var(--border-glass-strong)]',
        red: 'text-[var(--danger)] focus:ring-[var(--danger)]/20 border-[var(--border-glass-strong)]',
        amber: 'text-amber-500 focus:ring-amber-500/20 border-[var(--border-glass-strong)]',
        purple: 'text-purple-500 focus:ring-purple-500/20 border-[var(--border-glass-strong)]'
    };

    const variants = {
        default: 'rounded',
        rounded: 'rounded-full',
        switch: 'rounded-full'
    };

    if (variant === 'switch') {
        return (
            <div className={`flex items-start ${sizes[size].gap} ${className}`}>
                <label htmlFor={checkboxId} className="relative inline-flex items-center cursor-pointer">
                    <input
                        id={checkboxId}
                        name={name}
                        type="checkbox"
                        checked={checked}
                        onChange={handleChange}
                        disabled={disabled}
                        className="sr-only peer"
                    />
                    <div className={`
                        relative w-11 h-6 bg-[var(--bg-hover)] peer-focus:outline-none peer-focus:ring-4
                        peer-focus:ring-[var(--primary-500)]/25 rounded-full peer
                        peer-checked:after:translate-x-full peer-checked:after:border-[var(--bg-card)]
                        after:content-[''] after:absolute after:top-[2px] after:left-[2px]
                        after:bg-[var(--text-secondary)] after:border-[var(--border-subtle)] after:border after:rounded-full
                        after:h-5 after:w-5 after:transition-all
                        peer-checked:bg-[var(--primary-600)] peer-checked:after:bg-white peer-disabled:opacity-50 peer-disabled:cursor-not-allowed
                        ${disabled ? 'cursor-not-allowed' : 'cursor-pointer'}
                    `}></div>
                </label>
                {(label || description) && (
                    <div className="flex-1 min-w-0">
                        {label && (
                            <label
                                htmlFor={checkboxId}
                                className={`font-medium tracking-[-0.005em] text-[var(--text-primary)] ${sizes[size].text} ${disabled ? 'text-[var(--text-muted)] cursor-not-allowed' : 'cursor-pointer'}`}
                            >
                                {label} {required && <span className="text-[var(--danger)]">*</span>}
                            </label>
                        )}
                        {description && (
                            <p className="text-[var(--font-secondary)] text-[var(--text-secondary)] mt-1">{description}</p>
                        )}
                        {error && (
                            <p className="text-[var(--font-secondary)] text-[var(--danger)]/80 font-medium mt-1 flex items-center gap-1">
                                <svg className="w-4 h-4 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                                </svg>
                                {error}
                            </p>
                        )}
                    </div>
                )}
            </div>
        );
    }

    return (
        <div className={`flex items-start ${sizes[size].gap} ${className}`}>
            <div className="flex items-center h-6">
                <input
                    id={checkboxId}
                    name={name}
                    type="checkbox"
                    checked={checked}
                    onChange={handleChange}
                    disabled={disabled}
                    className={`
                        ${sizes[size].checkbox} ${colors[color]} ${variants[variant]}
                        bg-[var(--bg-surface)] border-2 focus:ring-[3px] focus:ring-offset-0
                        disabled:bg-[var(--bg-hover)] disabled:text-[var(--text-muted)] disabled:cursor-not-allowed
                        transition-all duration-[var(--duration-normal)] ease-[var(--ease-calm)] cursor-pointer
                        ${disabled ? 'cursor-not-allowed' : 'cursor-pointer'}
                        ${error ? 'border-[var(--danger)] text-[var(--danger)]' : ''}
                    `}
                />
            </div>
            {(label || description) && (
                <div className="flex-1 min-w-0">
                    {label && (
                        <label
                            htmlFor={checkboxId}
                            className={`font-medium tracking-[-0.005em] text-[var(--text-primary)] ${sizes[size].text} ${disabled ? 'text-[var(--text-muted)] cursor-not-allowed' : 'cursor-pointer'}`}
                        >
                            {label} {required && <span className="text-[var(--danger)]">*</span>}
                        </label>
                    )}
                    {description && (
                        <p className="text-[var(--font-secondary)] text-[var(--text-secondary)] mt-1">{description}</p>
                    )}
                    {error && (
                        <p className="text-[var(--font-secondary)] text-[var(--danger)]/80 font-medium mt-1 flex items-center gap-1">
                            <svg className="w-4 h-4 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                            </svg>
                            {error}
                        </p>
                    )}
                </div>
            )}
        </div>
    );
};

export default Checkbox;
